import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';

// Local debug switch for React Query diagnostics.
const DEBUG = false;

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (!DEBUG) return;

      const mainKey = Array.isArray(query.queryKey)
        ? query.queryKey[0]
        : query.queryKey;
      console.error(`[RQ] Error: ${mainKey} | ${error?.message || error}`);
    },
    onSettled: (data, error, query) => {
      if (!DEBUG) return;

      const mainKey = Array.isArray(query.queryKey)
        ? query.queryKey[0]
        : query.queryKey;
      const status = error ? `Error: ${error.message}` : 'Success';
      const dataSize = data
        ? Array.isArray(data)
          ? `${data.length} items`
          : 'Object'
        : 'No Data';
      console.log(`[RQ] Settled: ${mainKey} | ${status} | Data: ${dataSize}`);
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, variables, context, mutation) => {
      if (!DEBUG) return;

      console.error(
        `[RQ] Mutation Error: ${mutation.options.mutationKey} | ${
          error?.message || error
        }`,
      );
    },
  }),
  defaultOptions: {
    queries: {
      cacheTime: 1000 * 60 * 60 * 24, // 24 hours
      staleTime: 1000 * 60 * 5, // 5 minutes (reduces "ton of queries" noise)
      networkMode: 'offlineFirst',
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false, // Prevents queries from racing against OfflineSyncManager
      refetchInterval: false,
      refetchOnSettled: false,
      refetchIntervalInBackground: false,
      retry: (failureCount, error) => {
        if (failureCount > 3) return false;
        // Never retry client errors (4xx) - they are definitive failures.
        // e.g. after an application restart, usePrimaryFormDataQuery returns
        // 400 for the new applicantId (form not yet created). Retrying wastes
        // time and keeps isBlockingLoading=true, which prevents the portal
        // from finishing its loading sequence and renders it stuck.
        const status = error?.statusCode ?? error?.response?.status;
        if (status >= 400 && status < 500) return false;
        if (error?.affordaPortal?.reason === 'transport') return true;
        if (!error.response && error.message === 'Network Error') return true;
        return failureCount < 2;
      },
    },
    mutations: {
      networkMode: 'offlineFirst',
      retry: (failureCount, error) => {
        if (!error.response && error.message === 'Network Error') return true;
        return false;
      },
    },
  },
});

// For production debugging: track which queries are fetching globally.
if (DEBUG) {
  let lastFetchingCount = 0;
  queryClient.getQueryCache().subscribe(() => {
    const fetchingQueries = queryClient
      .getQueryCache()
      .getAll()
      .filter((q) => q.state.fetchStatus === 'fetching')
      .map((q) => (Array.isArray(q.queryKey) ? q.queryKey[0] : q.queryKey));

    // Only log when the count changes or we have active fetches.
    if (
      fetchingQueries.length !== lastFetchingCount ||
      fetchingQueries.length > 0
    ) {
      lastFetchingCount = fetchingQueries.length;
      console.log(
        `[RQ] Global Fetching (${fetchingQueries.length}): ${
          fetchingQueries.join('; ') || 'None'
        }`,
      );
    }
  });

  // Global debug helper for local console inspection.
  window.rqDebug = {
    client: queryClient,
    getFetching: () =>
      queryClient
        .getQueryCache()
        .getAll()
        .filter((q) => q.state.fetchStatus === 'fetching')
        .map((q) => q.queryKey),
    getQueries: () => queryClient.getQueryCache().getAll(),
  };
}

const localStoragePersister = createSyncStoragePersister({
  storage: window.localStorage,
});

export const PERSIST_OPTIONS = {
  persister: localStoragePersister,
  maxAge: 1000 * 60 * 60 * 24, // Persist cache for 24 hours
  buster: process.env.REACT_APP_VERSION || 'v1', // Increment this to invalidate old caches on structural changes
  dehydrateOptions: {
    // App-level offline replay is handled by `offlineQueue`; persisting paused
    // react-query mutations creates noisy duplicate mutation snapshots.
    shouldDehydrateMutation: () => false,
  },
};
